import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';

import PersonalisationSlotHeader from '../PersonalisationSlotHeader';
import { GoogleMapsLocationPicker } from '../GoogleMapsLocationPicker';
import { LocationMapGenerator } from '../LocationMapGenerator';
import { usePersonalisationContext } from '../../context/PersonalisationContext';

/**
 * PersonalisationLocationMapSlot component displays location picker for location map personalisation.
 * The location input stores data in a selection object.
 *
 * @param {Object} props - The component props
 * @param {Object} props.slot - The slot data
 * @returns {import('react').JSX.Element} The rendered component
 */
export default function PersonalisationLocationMapSlot({ slot }) {
  const { updateSelections, selections } = usePersonalisationContext();
  const locationMapGeneratorRef = useRef(/** @type {any} */(null));
  const [locationServiceUnavailableMessage, setLocationServiceUnavailableMessage] = useState('');

  // Get the current selection for this slot from the context.
  // The selection is stored as an object with location and locationMapImageUrl properties.
  const currentSelection = useMemo(() => {
    const slotSelection = selections.slots.find((s) => s.id === slot.id);

    // Parse the selection if it's a string (JSON) or use the object directly.
    if (!slotSelection?.selected) {
      return { location: '', locationPlace: null, locationMapImageUrl: null };
    }

    if (typeof slotSelection.selected === 'string') {
      try {
        return JSON.parse(slotSelection.selected);
      } catch {
        // If parsing fails, assume it's a simple string (legacy format).
        return { location: slotSelection.selected, locationPlace: null, locationMapImageUrl: null };
      }
    }

    // If it's already an object, use it directly.
    const selected = slotSelection.selected || {};
    // Use bracket notation to safely access properties.
    return {
      location: selected && typeof selected === 'object' ? selected['location'] || '' : '',
      locationPlace: selected && typeof selected === 'object' ? selected['locationPlace'] || null : null,
      locationMapImageUrl: selected && typeof selected === 'object' ? selected['locationMapImageUrl'] || null : null
    };
  }, [selections.slots, slot.id]);

  /**
   * Updates the selection in the context.
   * Merges new values with existing selection to preserve other fields.
   *
   * @param {Object} updates - Partial selection object with fields to update.
   */
  const handleSelectionUpdate = (updates) => {
    const updatedSelection = {
      ...currentSelection,
      ...updates
    };

    // Only save if at least one field has a value (including locationMapImageUrl).
    const hasValue =
      updatedSelection.location ||
      updatedSelection.locationMapImageUrl;

    updateSelections({
      slotId: slot.id,
      itemSelected: hasValue ? updatedSelection : null
    });
  };

  // Handler for location changes.
  // The place parameter is already the extracted/serialized data from GoogleMapsLocationPicker.
  const handleLocationChange = (address, place) => {
    // The place parameter is already extracted serializable data from gmp-place-autocomplete.
    // If it's not provided, try to read from the hidden input's data attribute.
    let locationPlaceData = place;

    if (!locationPlaceData) {
      // Fallback: read from the data attribute on the hidden input element.
      // This handles cases where the data was stored directly in the input.
      const locationInput = document.querySelector(`input[name="${slot.handle || slot.id}-location"]`);
      const locationPlaceAttr = locationInput ? locationInput.getAttribute('data-location-place') : null;
      if (locationInput && locationPlaceAttr) {
        try {
          locationPlaceData = JSON.parse(locationPlaceAttr);
        } catch (error) {
          console.error('Error parsing location place data from attribute:', error);
        }
      }
    }

    // Store both the formatted address string and the extracted place data.
    // Clear any previously generated image so the footer regenerates on "Continue".
    handleSelectionUpdate({
      location: address || '',
      locationPlace: locationPlaceData,
      locationMapImageUrl: null,
      locationMapImageUrlOriginal: null
    });

    // Also clear the image upload slot's positioned image so the editor
    // auto-opens with the newly generated B&W image on the next step.
    const imageUploadSlot = selections.slots.find((s) => s.slotType === 'IMAGE_UPLOAD');
    if (imageUploadSlot?.selected) {
      updateSelections({
        slotId: imageUploadSlot.id,
        itemSelected: null
      });
    }
  };

  /**
   * Handler for when location map generation succeeds.
   * Stores both the display (inverted) and original versions.
   *
   * @param {string | { display: string, original: string }} imageData - The generated image data
   */
  const handleLocationMapSuccess = (imageData) => {
    if (typeof imageData === 'string') {
      // Legacy format: just store as display version
      handleSelectionUpdate({
        locationMapImageUrl: imageData
      });
    } else if (imageData && typeof imageData === 'object') {
      // New format: store both versions
      handleSelectionUpdate({
        locationMapImageUrl: imageData.display, // Inverted version for display
        locationMapImageUrlOriginal: imageData.original // Original version for factory
      });
    }
  };

  const handleLocationAvailabilityChange = useCallback((state) => {
    if (state?.isUnavailable) {
      setLocationServiceUnavailableMessage(
        state.message || 'Location search is currently unavailable. Please try again in a moment.'
      );
      return;
    }
    setLocationServiceUnavailableMessage('');
  }, []);

  // Update ref storage when ref becomes available
  useEffect(() => {
    const refElement = document.querySelector(`[data-location-map-generator-ref="${slot.id}"]`);
    if (refElement) {
      // @ts-expect-error - Updating stored ref
      refElement._generatorRef = locationMapGeneratorRef;
    }
  }, [slot.id]);

  return (
    <div>
      <PersonalisationSlotHeader slot={slot} processHeading={false} />

      <GoogleMapsLocationPicker
        id={`${slot.id}-location`}
        name={`${slot.handle || slot.id}-location`}
        required={slot.required}
        labelText="Location"
        locationPickerHeading="Start typing to search for your location"
        onChange={handleLocationChange}
        onAvailabilityChange={handleLocationAvailabilityChange}
        defaultValue={currentSelection.location}
        autocompleteOptions={{
          requestOptions: {
            types: ['geocode'] // Restrict to addresses/locations (not businesses)
          }
          // No country restrictions - allow global locations as per requirements
        }}
      />

      <LocationMapGenerator
        ref={locationMapGeneratorRef}
        locationData={currentSelection}
        serviceUnavailableMessage={locationServiceUnavailableMessage}
        onSuccess={handleLocationMapSuccess}
      />
      
      {/* Store ref for footer to access via data attribute */}
      <div
        data-location-map-generator-ref={slot.id}
        ref={(el) => {
          if (el) {
            // @ts-expect-error - Storing ref for footer access
            el._generatorRef = locationMapGeneratorRef;
          }
        }}
        style={{ display: 'none' }}
      />
    </div>
  );
}

